import { FormProvider, useForm } from '@/components/react-hook-form';
import { useRemainingExemptionAmountForClient } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/hooks/useRemainingExemptionAmountForClient';
import { useHasGrantorWithStateTax } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/hooks/utilityHooks';
import { SplitDispositionModalResult } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/SplitDispositionModal';
import { SplitDispositionFormShape } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/SplitDispositionModal/SplitDispositionModal.types';
import { SplitModalShell } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/SplitDispositionModal/SplitModalShell';
import { TransferMethod } from '@/types/schema';

import { useBdgDyingClientId } from '../../hooks/useBdgDyingClientId';
import { BdgSplitAssetSelector } from './BdgSplitAssetSelector';
import { BdgSplitRecipientsSelector } from './BdgSplitRecipientsSelector';

interface SplitBeneficiaryDesignationsModalProps {
  isOpen: boolean;
  onClose: (result: SplitDispositionModalResult | null) => void;
  entityIdOverride?: string;
  isOnHypotheticalWaterfall: boolean;
}

const DEFAULT_FORM_VALUES: SplitDispositionFormShape = {
  distributionKind: 'percentage',
  distributionValue: null,
  formulaKind: '',
  maxDistributionAmount: null,
  selectedRecipients: [],
  assetGroupIds: [],
  assetStaticIds: [],
  insurancePolicyIds: [],
  ownershipStakeIds: [],
  dispositionAssetPercentage: null,
  transferMethod: TransferMethod.InKind,
};

export function SplitBeneficiaryDesignationsModal({
  isOpen,
  onClose,
  entityIdOverride,
  isOnHypotheticalWaterfall,
}: SplitBeneficiaryDesignationsModalProps) {
  const hasStateTax = useHasGrantorWithStateTax();
  const dyingClientId = useBdgDyingClientId();
  const { data: clientExemptionData } = useRemainingExemptionAmountForClient({
    clientId: dyingClientId,
  });

  const formMethods = useForm<SplitDispositionFormShape>({
    defaultValues: DEFAULT_FORM_VALUES,
  });

  return (
    <FormProvider {...formMethods}>
      <SplitModalShell
        isOpen={isOpen}
        onClose={onClose}
        hasStateTax={hasStateTax}
        // BDG recipient types are never state-specific (see the recipient card
        // and BdgSplitRecipientsSelector), so the table must not offer them
        // even when the grantor has state tax — which still drives formulas.
        recipientTypeHasStateTax={false}
        heading="Create evenly split beneficiaries"
        clientExemptionData={clientExemptionData}
        renderAssetSelector={() => (
          <BdgSplitAssetSelector entityIdOverride={entityIdOverride} />
        )}
        renderRecipientSelector={(errorMessage) => (
          <BdgSplitRecipientsSelector
            errorMessage={errorMessage}
            entityIdOverride={entityIdOverride}
            isOnHypotheticalWaterfall={isOnHypotheticalWaterfall}
          />
        )}
      />
    </FormProvider>
  );
}
